import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Inline JWT expiry check — cannot import authService here (circular dependency).
// authService imports api; api cannot import authService.
function isTokenExpired(token) {
  if (!token) return true;
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

function performLogout(reason) {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.setItem('sessionExpired', 'true');
  sessionStorage.setItem('logoutReason', reason);
  window.location.href = '/login';
}

// Request interceptor — proactive expiry check, attach auth token, remove Content-Type for multipart
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');

    if (token) {
      if (isTokenExpired(token)) {
        performLogout('expired');
        return Promise.reject(new axios.Cancel('Token expired'));
      }
      config.headers.Authorization = `Bearer ${token}`;
      // Notify idle timer that API activity occurred
      window.dispatchEvent(new Event('api-activity'));
    }

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // RC14.3 — Platform Operator tenant switch. Read directly from sessionStorage rather than
    // importing platformSwitchState here to avoid a circular dependency (that module has no
    // reason to import api.js, but keeping this inline keeps the dependency direction one-way).
    // The backend independently re-validates the caller's Tenant.Switch permission on every
    // request before honoring this header — it carries no authority on its own.
    const switchedTenant = sessionStorage.getItem('platformEffectiveTenant');
    if (switchedTenant) {
      try {
        const { id } = JSON.parse(switchedTenant);
        if (id) config.headers['X-Effective-Tenant-Id'] = String(id);
      } catch {
        // malformed sessionStorage value — ignore, don't attach a header
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — differentiated 401 handling; everything else propagates
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const code = error.response?.data?.code;

      if (code === 'USER_DISABLED') {
        performLogout('disabled');
      } else {
        performLogout('expired');
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Maps an Axios error to a user-friendly message.
 *
 * Never leaks backend exception text, stack traces, or internal details.
 * Safe to pass directly to Notification or Alert components.
 */
export function getErrorMessage(error) {
  if (!error) return 'An unexpected error occurred.';

  // Network / connectivity failures
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'The request timed out. Please check your connection and try again.';
    }
    if (error.message === 'Network Error') {
      return 'Cannot reach the server. Please check your connection.';
    }
    return 'A network error occurred. Please try again.';
  }

  const status = error.response.status;
  // Every 4xx handler in the backend's GlobalExceptionHandler already curates a safe,
  // user-facing message — IllegalArgumentException/IllegalStateException's own deliberately-
  // written text, MethodArgumentNotValidException's "field: reason" list, a hardcoded string for
  // malformed JSON, DataIntegrityViolationException's own curated (never-raw-SQL) text, etc. None
  // of them ever leak a stack trace or internal detail (that's what the 5xx catch-alls below are
  // for, which stay generic on purpose). Discarding that in favor of a generic fallback throws
  // away specific, actionable text a business-rule rejection deliberately included for the user
  // (e.g. "capture candidate interest, employment preference... first", not just "conflict").
  const backendMessage = error.response?.data?.message;

  // 401/403/5xx stay hardcoded, never trusting response.data.message — 401/403 reframe the
  // message for the frontend's own auth/session flow regardless of what the backend said, and
  // 5xx catch-alls are the one place an uncaught exception could theoretically carry something
  // not meant for display.
  if (status === 401) {
    if (error.response?.data?.code === 'USER_DISABLED') {
      return 'Your account has been disabled. Contact your administrator.';
    }
    return 'Your session has expired. Please log in again.';
  }
  if (status === 403) return 'You do not have permission to perform this action.';
  if (status === 503) return 'The AI Copilot is currently unavailable. Please try again in a moment.';
  if (status >= 500)  return 'A server error occurred. Please try again later or contact support.';

  if (backendMessage) return backendMessage;

  if (status === 400) return 'The request was invalid. Please check the form and try again.';
  if (status === 404) return 'The requested resource was not found.';
  if (status === 409) return 'This action conflicts with existing data. Please refresh and try again.';
  if (status === 422) return 'The submitted data is invalid. Please review and correct it.';

  return 'An unexpected error occurred. Please try again.';
}

export default api;
